import { useEffect } from 'react';
import { isAndroidWebView } from './in-app-browser';

/** How close to a scroller's edge a field may sit and still count as "visible".
 *  Small enough that a field flush against the keyboard is still moved. */
const VISIBLE_MARGIN_PX = 8;

/**
 * How much shorter than the layout viewport the visual one has to be before we
 * call it a keyboard.
 *
 * Tens of pixels are a collapsing URL bar or a form accessory bar on its own;
 * a software keyboard is hundreds (≈336 on a 390×844 iPhone). Lifting the sheet
 * for the small ones would make it twitch as the page scrolls.
 */
export const KEYBOARD_MIN_PX = 100;
/**
 * How long a freshly focused field waits for the viewport to react before the
 * hook concludes the keyboard opened WITHOUT resizing anything. iOS shrinks
 * the visual viewport within ~100–300 ms of focus; Chrome on Android shrinks
 * the layout viewport (`interactive-widget=resizes-content`) in about the
 * same time. What is left after 450 ms of silence on a touch screen is the
 * in-app WebView (Instagram, Telegram) that pans the whole window instead.
 */
export const ASSUME_KEYBOARD_AFTER_MS = 450;
/**
 * The keyboard the fallback assumes: 45% of the screen. Portrait Android
 * keyboards measure 40–48% across Gboard / Samsung / the Instagram WebView;
 * assuming a little less than the largest keeps the sheet's footer above all
 * of them without wasting a third of a phone that has a shorter one.
 */
export const ASSUMED_KEYBOARD_RATIO = 0.45;
/**
 * The ceiling every sheet had before this hook existed: 92% of the viewport.
 *
 * ⚠️ A NUMBER, applied in px — never the `92dvh` this hook used to WRITE.
 * `dvh` landed in Chrome 108, and on Chrome 101 (Android 9, Xiaomi Mi 8 Lite —
 * a real buyer's phone, measured 2026-09-26) `CSS.supports('height', '1dvh')`
 * is false. `max-height: var(--sheet-max)` then parses fine and is thrown away
 * at COMPUTED-VALUE time, because the substituted `min(92dvh, 234px)` is not a
 * valid length there: the declaration becomes invalid-at-computed-value-time
 * and `max-height` computes to `none`. So the sheet was never capped in ANY
 * state on that build, while `--sheet-lift` — plain px — was applied exactly
 * as written. That asymmetry is the whole bug: 483px of panel, lifted 202px,
 * inside a 448px screen, header at −237.
 */
export const SHEET_MAX_RATIO = 0.92;
/**
 * How long after a touch/pointer press a `focusin` can still be that press's
 * doing — the window inside which "a field took focus" may be read as "the user
 * tapped a field and a keyboard is coming".
 *
 * 1500 ms is generous on purpose: the gesture is `touchstart`, the focus lands
 * on `touchend`/`click` after the tap and any 300 ms click delay, and a slow
 * phone mid-render can stretch that. It only has to be shorter than the gap
 * between a tap and an unrelated asynchronous focus, which is a network round
 * trip — the one measured below was **the tokenize response**.
 */
export const GESTURE_MAX_AGE_MS = 1500;

/**
 * How long `visualViewport` may stay silent before the height it reports stops
 * being evidence of anything.
 *
 * A real keyboard opening or closing fires `resize`; iOS fires `scroll` on every
 * frame of a pan. 1.5 s of complete silence therefore means nothing has moved —
 * and if the number still claims a keyboard, the number is left over from one
 * that has gone.
 */
export const VV_STALE_MS = 1500;

/**
 * The focused TEXT field, or null — `input`, `textarea`, `select` and anything
 * `contenteditable`.
 *
 * ⚠️ A `<select>` counts: iOS shows it a wheel picker, which shrinks the visual
 * viewport exactly like a keyboard does.
 *
 * ⚠️ Known limitation: focus inside a cross-origin `<iframe>` reads as the
 * `<iframe>` element, not the field within it, so a keyboard opened for a
 * third-party form would look like «nothing focused». There is no such form in
 * this app today (checked 2026-09-26: the only iframe is the Telegram login
 * widget, which takes no typed input), and the Payme/Click/OCTO pages are
 * navigations, not frames. Add `iframe` here the day one appears.
 */
/**
 * How long a lift outlives the field that has just lost the focus.
 *
 * Since «a lift needs a focused field» (2026-09-26) the lift was dropped in the
 * `focusout` handler itself. On Android a tap on a button or a link MOVES the
 * focus to it, so the field blurs on the tap's own mousedown — and the sheet,
 * raised above the keyboard, fell by the whole lift between the press and the
 * release. The engine then delivers the click to the common ancestor of what was
 * under the finger at each end, i.e. to the sheet's body, and the control is
 * never activated.
 *
 * Prod, 2026-10-02 21:00:57, Instagram on Android (PostHog 01a0fd58-…): touch on
 * «Продолжить на Octobank» at .536–.609, field blur at .624, `--sheet-lift: 0px`
 * at .629, click at .682 on the sheet's `<div>`. No order; the buyer left.
 * Reproduced on prod with the same viewport: lift 284px → 0px in the same
 * millisecond as the tap's focusout.
 *
 * The sheet must not move while a tap that blurred a field is still landing. A
 * tap is press-to-click in well under 200 ms; the blur timer below re-reads the
 * geometry at 350 ms, by which time this window has closed, so a lift with no
 * field behind it still goes away — just not under the finger. A sheet that
 * opens with the focus on a button (the stale-WKWebView case this rule was
 * written for) has no blur behind it and gets no grace at all.
 */
export const BLUR_LIFT_GRACE_MS = 300;

export function focusedField(): HTMLElement | null {
  const el = typeof document === 'undefined' ? null : document.activeElement;
  if (!(el instanceof HTMLElement)) return null;
  if (el.matches('input, textarea, select')) return el;
  return el.isContentEditable ? el : null;
}

/** What `watchKeyboardGeometry` hands back. */
export interface KeyboardGeometry {
  /** Re-read `innerHeight`. Call on EVERY geometry read — this is what learns. */
  note(): void;
  /** `innerHeight` is materially below the tallest seen: a keyboard the engine
   *  has handled by resizing the page. */
  shrank(): boolean;
  /** `innerHeight` is at the tallest seen: the page is at full height. */
  atTallest(): boolean;
  /** This engine has been SEEN to shrink `innerHeight` for a keyboard, so its
   *  `visualViewport.height` is not the keyboard and may be stale. */
  resizesForKeyboard(): boolean;
  /** Call from every `visualViewport` resize/scroll handler. */
  vvReported(): void;
  /** `visualViewport.height` claims a keyboard that cannot be real. */
  visualViewportStale(): boolean;
}

/**
 * WHAT THIS BROWSER DOES WITH A KEYBOARD, LEARNT WHILE THE SHEET IS OPEN.
 *
 * Two engines, two mechanisms, and one of them lies afterwards:
 *
 * - **iOS Safari** shrinks the VISUAL viewport and leaves `innerHeight` alone.
 *   `visualViewport.height` IS the keyboard, and that is the whole premise this
 *   file was built on.
 * - **Chrome for Android, and iOS WKWebViews inside host apps**, shrink the
 *   LAYOUT viewport — `innerHeight` — so both viewports stay equal. Then, when
 *   the keyboard closes, `innerHeight` comes back in steps while
 *   `visualViewport.height` **lags, and can simply stop updating**.
 *
 * Measured on a paying customer's iPhone 15 Pro (iOS 26.6.1, Instagram's in-app
 * browser = WKWebView, 393×778, 2026-09-26 15:17:25 UTC, PostHog rrweb):
 * `innerHeight` had gone 749 → 402 for the keyboard on the register page and
 * back to 778 when it closed, but `visualViewport.height` was still reporting
 * ~402 minutes later. The card modal then opened with NOTHING focused and no
 * keyboard anywhere, and `keyboardUp()` — a comparison of the two heights — was
 * true: 778 − 402 = 376. The root got `--sheet-max: 390px; --sheet-lift: 376px`,
 * which is the owner's screenshot: a 390px panel floating 376px up with the page
 * showing underneath.
 *
 * So: the moment `innerHeight` is seen materially below its own maximum WHILE a
 * field is focused, this browser has proved it resizes the layout viewport for
 * the keyboard — and from then on its visual viewport is not consulted for the
 * inset at all. Safari never shrinks `innerHeight` for a keyboard, so the flag
 * can only ever be set by the engines whose numbers go stale.
 *
 * ⚠️ `learned` is never unset. One demonstration is proof about the engine, and
 * a browser does not change mechanism mid-sheet. A ROTATION resets the tallest
 * height (a portrait maximum says nothing about landscape) but NOT the flag.
 *
 * ⚠️ Per-effect, like `watchUserGestures`: a fresh sheet has to learn again,
 * because it has no way to know what happened before it existed. That is why
 * `focusedField()` and `visualViewportStale()` carry the /plans case on their
 * own — the card modal opened long after the keyboard that poisoned the reading.
 */
export function watchKeyboardGeometry(vv?: { height: number } | null): KeyboardGeometry {
  const innerH = () => (typeof window === 'undefined' ? 0 : window.innerHeight);
  const innerW = () => (typeof window === 'undefined' ? 0 : window.innerWidth);
  let tallest = innerH();
  let lastW = innerW();
  let learned = false;
  /*
   * ZERO, not `Date.now()`, and that is deliberate: at mount this effect has
   * received no `visualViewport` event at all, so whatever height it reads was
   * produced before the sheet existed. A sheet that opens onto a stale number
   * must be able to distrust it on its FIRST measurement rather than after 1.5 s
   * of showing the wrong shape.
   */
  let lastVvAt = 0;
  return {
    note() {
      const h = innerH();
      // A ROTATION is a new page, not a keyboard.
      if (innerW() !== lastW) {
        lastW = innerW();
        tallest = h;
        return;
      }
      if (h > tallest) {
        tallest = h;
        return;
      }
      if (!learned && h < tallest - KEYBOARD_MIN_PX && focusedField()) learned = true;
    },
    shrank: () => innerH() < tallest - KEYBOARD_MIN_PX,
    atTallest: () => innerH() >= tallest,
    resizesForKeyboard: () => learned,
    vvReported() {
      lastVvAt = Date.now();
    },
    visualViewportStale: () =>
      vv != null &&
      // The reading claims a keyboard…
      vv.height < innerH() - KEYBOARD_MIN_PX &&
      // …but nothing is focused, so there is nothing for a keyboard to be for…
      !focusedField() &&
      // …the page is at its full height, so no engine is hiding one…
      innerH() >= tallest &&
      // …and the visual viewport has not moved in a long time. A keyboard that
      // was really opening or closing would be firing events.
      Date.now() - lastVvAt > VV_STALE_MS,
  };
}

/** What `watchUserGestures` hands back. */
export interface GestureWatch {
  /** True when a press landed within `maxAgeMs`. */
  recent(): boolean;
  stop(): void;
}

/**
 * WAS THE FOCUS THE USER'S DOING?
 *
 * ⚠️ The assumed keyboard's real premise is not «a field is focused». It is «the
 * user tapped a field, so a keyboard is coming even though nothing moved». Focus
 * alone is a much weaker claim, and on iOS it is a FALSE one: a programmatic
 * `.focus()` outside a user gesture opens no keyboard at all, in any engine.
 *
 * Measured on the owner's iPhone (Chrome iOS 153 / WebKit, 393×665, 2026-09-26
 * 11:46 UTC, PostHog rrweb): he tapped «Оплатить» — button focused, card fields
 * blurred, no keyboard — Payme refused the card (`payme_-32999`, Visa), and the
 * modal's error handler called `revealField` → `.focus()` on the card number at
 * t=106.82s. Nothing opened and nothing resized, so 450 ms later the assumption
 * fired on an idle page: `--sheet-lift: 299px` (round(665 × 0.45)),
 * `--sheet-max: 354px`, `--kb-pad: 299px`. He photographed a 354px sheet floating
 * 299px up with the page showing underneath and its header scrolled away, no
 * keyboard anywhere on screen.
 *
 * Autofill, a validation handler, a step change and a `ref.focus()` on mount are
 * all the same shape. So the press is recorded and the focus has to follow it.
 *
 * ⚠️ Per-effect, not module-level, and deliberately: the state must die with the
 * sheet. A module-level timestamp would leak one sheet's tap into the next one's
 * decision — and, in tests, one case's tap into the next case's.
 *
 * Capture phase and passive: nothing here calls `preventDefault`, and a listener
 * on the way DOWN cannot be stopped by a handler that swallows the event.
 */
export function watchUserGestures(maxAgeMs: number = GESTURE_MAX_AGE_MS): GestureWatch {
  let at = 0;
  const mark = () => {
    at = Date.now();
  };
  const types = ['touchstart', 'pointerdown', 'mousedown'] as const;
  if (typeof document !== 'undefined') {
    for (const t of types) document.addEventListener(t, mark, { capture: true, passive: true });
  }
  return {
    recent: () => at > 0 && Date.now() - at <= maxAgeMs,
    stop: () => {
      if (typeof document === 'undefined') return;
      for (const t of types) document.removeEventListener(t, mark, { capture: true });
    },
  };
}

/**
 * The VirtualKeyboard API (Chromium 94+): once a page sets `overlaysContent`,
 * the keyboard stops resizing any viewport and reports its own rectangle on
 * `geometrychange` instead. Not in `lib.dom` yet, hence the local shape; a
 * build without it simply returns null here and keeps the assumption above.
 */
interface VirtualKeyboardLike {
  overlaysContent: boolean;
  boundingRect: { height: number };
  addEventListener(type: 'geometrychange', fn: () => void): void;
  removeEventListener(type: 'geometrychange', fn: () => void): void;
}

function virtualKeyboardOf(nav: Navigator): VirtualKeyboardLike | null {
  const vk = (nav as Navigator & { virtualKeyboard?: VirtualKeyboardLike }).virtualKeyboard;
  return vk && typeof vk.addEventListener === 'function' ? vk : null;
}

/** The nearest ancestor that actually scrolls, or null. jsdom reports no
 *  computed `overflow-y`, so this finds nothing there — which is why every
 *  caller that knows its scroller passes it. */
function nearestScroller(el: HTMLElement): HTMLElement | null {
  let p: HTMLElement | null = el.parentElement;
  while (p && !/(auto|scroll)/.test(getComputedStyle(p).overflowY)) p = p.parentElement;
  return p;
}

/**
 * Bring `el` into view by scrolling the SHEET'S OWN SCROLLER — never the page.
 *
 * ⚠️ NEVER `scrollIntoView`, and this function is the single place that rule
 * is implemented. `scrollIntoView` scrolls EVERY scrollable ancestor, the
 * document included, and on iOS `body{overflow:hidden}` does not hold the page
 * — which is the whole reason these sheets are wrapped in `<RemoveScroll>`.
 * Panning the layout viewport under a `position:fixed` overlay is how the sheet
 * ends up PAINTED in one place and HIT-TESTED in another: «дёргает и я опять
 * вверху модалки» on the number→expiry auto-advance (2026-09-16), and then, once
 * the guest address block moved directly on top of the card number, a tap on
 * «Номер карты» that focused nothing and opened no keyboard while the email
 * above it worked (Igor, iPhone, 2026-09-20). Chromium cannot reproduce that:
 * it honours `body{overflow:hidden}`, so there is nothing for it to pan.
 *
 * Already fully inside the scroller → nothing moves. This runs right after the
 * tap that focused the field, and a field that jumps between two taps is a tap
 * that misses.
 *
 * Returns false when no scroller was found, so a caller with nowhere else to
 * scroll can decide what to do about it.
 */
export function centreInScroller(el: HTMLElement, scroller?: HTMLElement | null): boolean {
  const p = scroller ?? nearestScroller(el);
  if (!p || typeof p.scrollTo !== 'function') return false;
  const pr = p.getBoundingClientRect();
  const er = el.getBoundingClientRect();
  if (er.top >= pr.top + VISIBLE_MARGIN_PX && er.bottom <= pr.bottom - VISIBLE_MARGIN_PX) {
    return true;
  }
  const top = p.scrollTop + (er.top - pr.top) - (p.clientHeight - er.height) / 2;
  // Instant, never animated. A smooth scroll left the sheet parked at its
  // header for ~0.3 s (longer on a phone, where the keyboard slide comes first)
  // with the tapped field under the keyboard — and the user's instinctive touch
  // during that window CANCELS the animation for good, stranding them at the
  // top of the sheet («перекидывает вверх, а не на поле»). Measured on the live
  // bundle 2026-09-16 at 390×844: smooth = field visible after 322 ms and 0 px
  // further once a touch lands at 250 ms; instant = 220 ms, interruption-proof.
  p.scrollTo({ top: Math.max(0, top) });
  return true;
}

/**
 * Cap a bottom-sheet modal to the VISUAL viewport while it is open, exposed as
 * the `--sheet-max` custom property on the document root.
 *
 * Why this exists. iOS shrinks `window.visualViewport` when the software
 * keyboard opens but leaves the LAYOUT viewport — and therefore `dvh` — exactly
 * as it was. A sheet sized `max-h-[92dvh]` and anchored to the bottom then
 * extends above the top of the screen: its header (price, plan, renewal dates)
 * is off-screen, and because the panel still FITS inside its own max-height,
 * `scrollHeight === clientHeight` — there is nothing to scroll. The content is
 * not "above the scroll position", it is outside the window.
 *
 * That combination also disarms the only escape: `useSwipeDownToDismiss` arms a
 * dismiss drag whenever `scrollTop <= 0`, which is permanently true on a panel
 * that cannot scroll. So every attempt to pull the top into view closed the
 * sheet instead. Reported on an iPhone 2026-09-05: «листнуть вверх не могу,
 * только если смахиваю (закрываю) модалку».
 *
 * With the cap applied the panel genuinely overflows, native scrolling works,
 * and the swipe gesture goes back to arming only at the real top of the content.
 *
 * Set on the root rather than the panel because the panels already carry an
 * inline `style` from the swipe transform; custom properties inherit, so a
 * re-render mid-gesture cannot clobber this.
 *
 * The 12px trim keeps the rounded top edge and the drag pill clear of the
 * viewport edge. Wherever `visualViewport` is missing the property is simply
 * never set and the panel's own Tailwind fallback applies — which is why that
 * fallback is spelled `max-h-[var(--sheet-max,88vh)]
 * supports-[height:1dvh]:max-h-[var(--sheet-max,88dvh)]` on every sheet: a
 * `dvh` fallback is no cap at all on Chrome < 108 (see `SHEET_MAX_RATIO`).
 */
export function useVisualViewportSheetHeight(
  open: boolean,
  /**
   * The element that actually SCROLLS inside the sheet.
   *
   * ⚠️ Pass it. Since the pay button became a pinned footer (2026-09-20) the
   * panel is `flex-col overflow-hidden` and no longer scrolls — its `scrollTop`
   * is permanently 0 — while the body beside the footer is the scroller. The
   * fallback below walks up from the focused field looking for the nearest
   * `overflow-y: auto|scroll` ancestor, which still FINDS that body in a
   * browser; the argument exists because the walk depends on computed styles
   * (jsdom reports none, so the fallback silently finds nothing and the unit
   * test cannot see the regression) and because a caller that knows its
   * scroller should not be re-derived from the DOM on every keystroke.
   *
   * Omitted → the walk, i.e. exactly the behaviour every caller had before.
   */
  scrollerRef?: { current: HTMLElement | null },
): void {
  useEffect(() => {
    if (!open) return;
    const vv = typeof window === 'undefined' ? null : window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    // `min(92% of the layout viewport, …)`: the visual-viewport cap only ever
    // LOWERS the old ceiling. Without the keyboard, `vv.height − 12` is taller
    // than 92%, and on a short desktop window a tall modal would then overrun
    // the overlay's padding by a few px. With the keyboard up, the visible-area
    // term is the smaller one and wins.
    // "Keyboard is up" ≈ the visual viewport is materially shorter than the
    // layout viewport. Desktop and Android builds that resize the layout viewport
    // instead never satisfy this, so they keep their native focus handling.
    //
    // ⚠️ …AND ONLY WHILE THIS ENGINE HAS NOT PROVED THAT IT RESIZES THE LAYOUT
    // VIEWPORT FOR THE KEYBOARD (2026-09-26). Once it has, the comparison is
    // between a live `innerHeight` and a `visualViewport.height` that lags behind
    // it and can stop updating altogether — see `watchKeyboardGeometry`, where a
    // WKWebView reporting a keyboard that had closed minutes earlier lifted a
    // paying customer's card modal 376px off an idle page.
    /*
     * THE TALLEST LAYOUT VIEWPORT SEEN SINCE THIS SHEET OPENED, and what this
     * engine does with a keyboard. Shared with the panel-cap and page-pad hooks
     * so the three can never disagree about what a keyboard is.
     *
     * On Chrome for Android with our `interactive-widget=resizes-content`
     * viewport meta, `window.innerHeight` IS the keyboard: the engine shrinks
     * the LAYOUT viewport, both viewports stay equal so `keyboardUp()` is never
     * true, and `dvh` follows along by itself. Nothing there needs a lift — the
     * sheet is already pinned to the bottom of a shorter page.
     *
     * The focus-time snapshot (`focusH0`) cannot see that, because it only
     * measures the step this focus caused. Swapping the ИНН field's numeric pad
     * for «Название организации»'s text keys plus the suggestion strip took
     * 534 → 448 — an 86px step, under `KEYBOARD_MIN_PX` — so with a keyboard
     * plainly up the hook concluded there was none and assumed one ON TOP:
     * `--sheet-lift: 202px` on a panel already inside 448px, top at −237
     * (Chrome 101, 393×744, 2026-09-26). Measured against the TALLEST height
     * instead, 448 < 744 − 100 says «a keyboard is up and the engine has
     * already handled it».
     *
     * ⚠️ Seeded at mount, so a sheet that OPENS with the keyboard already up
     * starts with the short height as its tallest and can still assume once.
     * It self-heals: the closing keyboard is a `resize`, `apply()` notes the
     * taller height, and every later focus is guarded. Opening a payment sheet
     * from a button that just blurred a field is the only way in, and the cost
     * is one keyboard cycle — not a wedged sheet.
     */
    const layout = watchKeyboardGeometry(vv);
    const keyboardUp = () =>
      !layout.resizesForKeyboard() && vv.height < window.innerHeight - KEYBOARD_MIN_PX;
    /*
     * THE TWO PRECONDITIONS OF THE ASSUMPTION, both added 2026-09-26 after it
     * fired on an idle iPhone (see `watchUserGestures`).
     *
     * `androidWebView` — the assumption exists for ONE population: an Android
     * WebView (Instagram, Telegram) where a real keyboard resizes nothing. Every
     * iOS engine — Safari, Chrome iOS, and WKWebView inside the in-app browsers —
     * shrinks the VISUAL viewport for a real keyboard, which `keyboardUp()`
     * already reads. So on iOS «a field is focused and nothing shrank» is not an
     * unmeasurable keyboard, it is positive evidence of NO keyboard, and guessing
     * one there can only ever be wrong. Same UA test the VirtualKeyboard opt-in
     * below uses, hoisted so the two cannot drift apart.
     */
    const androidWebView = isAndroidWebView();
    const gestures = watchUserGestures();
    /*
     * `--sheet-lift`: how far the visual viewport's bottom edge sits ABOVE the
     * layout viewport's — i.e. the keyboard, LESS any pan iOS applied to keep a
     * field visible (the pan moves the `position:fixed` overlay up with it, so
     * the sheet needs that much less lift and double-lifting it puts its top off
     * the screen). A bottom sheet is `position:fixed; bottom:0` against the
     * LAYOUT viewport, and iOS does not move fixed boxes above the keyboard:
     * capping the height alone (the first fix) left a short sheet sitting almost
     * entirely UNDER the keyboard, with ~80px peeking out — strictly worse than
     * the tall one it replaced (iOS, 2026-09-05 12:54). The panels consume this
     * as `margin-bottom`, which under `items-end` raises them onto the visible
     * area.
     *
     * ⚠️ PURE GEOMETRY. Never "is something focused" — WebKit takes the keyboard
     * away on «Done» and on the scroll-to-dismiss gesture WITHOUT blurring the
     * input, so focus says "keyboard" long after the keyboard has gone. That is
     * the empty keyboard-sized band Igor photographed above the pinned footer
     * with the card number and 09/35 already typed (iPhone 15, 2026-09-21).
     */
    const keyboardInset = () =>
      Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
    /*
     * THE ASSUMED KEYBOARD (2026-09-22). Inside the Instagram and Telegram
     * in-app browsers on Android the keyboard opens and NOTHING here moves:
     * the host app pans its whole window instead of resizing the WebView, so
     * `visualViewport` and `innerHeight` both stay put, `keyboardUp()` stays
     * false, and a bottom sheet keeps its full 92dvh — half of it now under
     * the keyboard, with the pay button in that half and the scroller unable
     * to reach it (tester, Android 16, 2026-09-22: «клавиатура перекрывает
     * поля, скролл не работает, кнопка Оплатить не поверх»). When a field
     * has had focus for `ASSUME_KEYBOARD_AFTER_MS` on a touch screen and no
     * viewport has shrunk, the hook assumes a keyboard of
     * `ASSUMED_KEYBOARD_RATIO` and drives the same two variables from it: the
     * sheet is capped to what is left above it and lifted clear of it, so the
     * fields AND the footer sit in the visible top half. A real `resize`
     * (either viewport shrinking) replaces the assumption at once; the blur
     * that ends typing drops it.
     *
     * ⚠️ FOUR PRECONDITIONS, and every one of them was learnt from a sheet that
     * lifted when it should not have. The assumption needs: an Android WebView
     * (`androidWebView` — iOS shrinks the visual viewport, so there it is a
     * measurement, not a guess), a recent user press (`gestures.recent()` — a
     * programmatic `.focus()` opens no keyboard), a layout viewport still at its
     * tallest (`layout.shrank()` — otherwise the engine already handled the
     * keyboard by resizing), and a coarse pointer. The two added on 2026-09-26
     * are the ones that cost real money: see `watchUserGestures` for the iPhone
     * recording and `tallestInnerH` for the Chrome-on-Android one.
     */
    let assumed = 0;
    let assumedAtInnerH = 0;
    // True while `assumed` is a MEASUREMENT (VirtualKeyboard API), not a guess.
    let measured = false;
    const keyboardShown = () => keyboardUp() || assumed > 0;
    /*
     * ⚠️ A LIFT NEEDS A FOCUSED FIELD (2026-09-26). Read the note on
     * `keyboardInset` above first: geometry, not focus, decides whether a
     * keyboard is GONE. This is the opposite guard, and both are true at once —
     *
     *   keyboard  ⟺  a field is focused  AND  the geometry says so
     *
     * — so «focused but the viewport came back» is still no keyboard (the «Done»
     * button, unchanged), and «the viewport claims one but nothing is focused» is
     * now no keyboard either. The second half is what a stale WKWebView reading
     * looks like: a paying customer's card modal opened with the focus on a
     * BUTTON and was lifted 376px off an idle page, because
     * `visualViewport.height` still held a value from a keyboard that had closed
     * minutes before (see `watchKeyboardGeometry`).
     *
     * Only the LIFT is suppressed here. A cap that is too short is ugly; a lift
     * with nothing under it puts the sheet in the middle of the screen with the
     * page showing through, and the full reset for that reading is
     * `layout.visualViewportStale()` in `apply()`.
     */
    // …or one that had it a moment ago: see `BLUR_LIFT_GRACE_MS`.
    let fieldBlurredAt = 0;
    const justBlurred = () =>
      fieldBlurredAt > 0 && Date.now() - fieldBlurredAt < BLUR_LIFT_GRACE_MS;
    const inset = () =>
      focusedField() || justBlurred() ? (keyboardUp() ? keyboardInset() : assumed) : 0;
    const visibleHeight = () => (keyboardUp() ? vv.height : window.innerHeight - assumed);
    /*
     * Last written values, so an unchanged `apply()` writes NOTHING.
     *
     * `apply` is bound to `visualViewport.scroll`, which iOS fires on every
     * frame of a pan — and `--sheet-lift` is the panel's `margin-bottom`, so an
     * identical re-write still dirties layout on a box the buyer may have a
     * finger on. Comparing first is free and makes the sheet hold still
     * whenever the numbers have not actually moved.
     */
    let lastMax = '';
    let lastLift = '';
    let seen = '';
    /*
     * A low-rate poll for as long as the sheet is LIFTED. The event-driven path
     * and the 1.5 s watch after a focus change cover the keyboard opening; they
     * do not cover a keyboard that leaves while the field keeps its focus and
     * nothing fires — iOS's own «save this card?» prompt (Igor, iPhone,
     * 2026-09-24: the keyboard went, the sheet stayed lifted mid-screen with
     * the page showing under it; the tap back into the code field cured it),
     * or the second field of a row whose pan iOS never reports. Reading three
     * numbers every 400 ms is not the per-frame `scroll` chase this file
     * refuses; it stops the moment there is nothing left to correct.
     *
     * ⚠️ It also runs while the sheet is CAPPED in the layout-resize mode
     * (2026-09-26), where there is no lift to watch. That is the mode whose
     * events go missing — the WKWebView whose `innerHeight` came back 749 → 778
     * in steps while `visualViewport` fell silent for good — so a cap left behind
     * at 402px when the page is 778px tall needs something to notice. The normal
     * iOS path is unchanged: no lift, no poll.
     */
    const POLL_MS = 400;
    let pollTimer = 0;
    /** Is there anything left for the poll to correct? */
    const polling = () => lastLift !== '' || (layout.resizesForKeyboard() && lastMax !== '');
    function pollTick(): void {
      pollTimer = 0;
      if (!polling()) return;
      const key = geometryKey();
      if (key !== seen) {
        seen = key;
        if (assumed && !measured && (keyboardUp() || window.innerHeight !== assumedAtInnerH)) {
          assumed = 0;
        }
        apply();
        if (settling()) centreFocused();
      }
      if (polling()) pollTimer = window.setTimeout(pollTick, POLL_MS);
    }
    function startPoll(): void {
      if (!pollTimer) pollTimer = window.setTimeout(pollTick, POLL_MS);
    }
    function stopPoll(): void {
      window.clearTimeout(pollTimer);
      pollTimer = 0;
    }
    /*
     * `?sheetdebug=1`: a fixed readout of the numbers this hook lives on, for a
     * tester's screenshot when the lift looks wrong on a phone we cannot hold.
     * Off by default; costs nothing when off.
     */
    const debugOn =
      typeof window !== 'undefined' && /[?&]sheetdebug=1/.test(window.location.search);
    let debugEl: HTMLElement | null = null;
    function renderDebug(): void {
      if (!debugOn) return;
      if (!debugEl) {
        debugEl = document.createElement('pre');
        debugEl.setAttribute('data-testid', 'sheet-debug');
        debugEl.style.cssText =
          'position:fixed;left:4px;top:4px;z-index:9999;margin:0;padding:4px 6px;font:11px/1.3 monospace;' +
          'background:rgba(0,0,0,.72);color:#fff;border-radius:6px;pointer-events:none;white-space:pre';
        document.body.appendChild(debugEl);
      }
      const ae = document.activeElement;
      debugEl.textContent =
        `innerH ${window.innerHeight} vvH ${vv ? Math.round(vv.height) : '-'} vvTop ${vv ? Math.round(vv.offsetTop) : '-'} ` +
        `scrollY ${Math.round(window.scrollY)}\nlift ${lastLift || '-'} max ${lastMax || '-'} ` +
        `assumed ${assumed}${measured ? '*' : ''} focus ${ae ? ae.tagName.toLowerCase() : '-'}`;
    }
    /*
     * BACK TO NORMAL — both properties REMOVED, not rewritten to a
     * keyboard-less value.
     *
     * A cap of ~92% of the screen and no lift are what the sheet had before
     * this hook existed, and removing the properties is how each panel's own
     * Tailwind fallback states exactly that — so this is the same layout with
     * one fewer thing that can go stale. It also means a `--sheet-lift` left
     * behind by an unmount, a second sheet or a resize we missed cannot survive
     * the next keyboard-less measurement.
     *
     * ⚠️ Which is why those fallbacks must not be a bare `dvh`: removing the
     * properties on a build that resizes the LAYOUT viewport happens exactly
     * WHILE the keys are up, and on Chrome < 108 a `dvh` fallback is no cap at
     * all rather than a generous one. Hence the `supports-[height:1dvh]` pair on
     * every panel.
     */
    const reset = () => {
      if (lastMax !== '') {
        lastMax = '';
        root.style.removeProperty('--sheet-max');
      }
      if (lastLift !== '') {
        lastLift = '';
        root.style.removeProperty('--sheet-lift');
      }
      stopPoll();
      renderDebug();
    };
    /*
     * What the geometry watch below compares against. `apply()` refreshes it,
     * so a change an event already handled is never handled a second time by
     * the watch (that double-placed the field once per resize).
     */
    const geometryKey = () =>
      // ⚠️ FOCUS IS PART OF THE KEY (2026-09-26). Since a lift needs a focused
      // field, `apply()`'s output changes when the focus does — so a watch or
      // poll tick that compares only the three numbers would sit on a lift the
      // buyer has already moved away from. That is the tick that has to notice
      // «focus went to a button», which is the customer's /plans case.
      `${window.innerHeight}|${vv.height}|${vv.offsetTop}|${focusedField() ? 1 : 0}`;
    const setMax = (value: string) => {
      if (value === lastMax) return;
      lastMax = value;
      root.style.setProperty('--sheet-max', value);
    };
    const setLift = (value: string) => {
      if (value === lastLift) return;
      lastLift = value;
      root.style.setProperty('--sheet-lift', value);
    };
    const dropLift = () => {
      if (lastLift === '') return;
      lastLift = '';
      root.style.removeProperty('--sheet-lift');
    };
    const apply = () => {
      layout.note();
      seen = geometryKey();
      /*
       * MODE A — THIS ENGINE RESIZES THE LAYOUT VIEWPORT FOR THE KEYBOARD, and
       * has been seen doing it (2026-09-26). Chrome for Android always; the
       * WKWebViews inside iOS host apps too, which is how the reading that lifted
       * a paying customer's card modal 376px off an idle page came to be stale in
       * the first place.
       *
       * Here `innerHeight` IS the keyboard and `visualViewport.height` is not
       * consulted at all — not for the inset, not for the cap. The sheet is
       * already pinned to the bottom of a shorter page, so there is NOTHING to
       * lift; the cap is `innerHeight − 12` in px while the page is short, and
       * removed the moment it is tall again. Same arithmetic as the panel hook's
       * layout-resize branch, so a sheet whose panel switches owners mid-keyboard
       * does not jump.
       */
      if (layout.resizesForKeyboard()) {
        if (!layout.shrank()) {
          reset();
          return;
        }
        setMax(`${Math.round(window.innerHeight - 12)}px`);
        dropLift();
        startPoll();
        renderDebug();
        return;
      }
      // Measured, on every single call. `keyboardUp()` is the HEIGHT test and
      // `keyboardInset()` the OFFSET one: a fully panned layout viewport needs
      // no lift at all while its keyboard is still very much up, so the two
      // cannot be collapsed into one number.
      if (!keyboardShown()) {
        reset();
        return;
      }
      /*
       * MODE B — THE READING IS STALE. Nothing is focused, the page is at its
       * full height, and `visualViewport` has said nothing for `VV_STALE_MS`, yet
       * its height still claims a keyboard. A keyboard that was really there
       * would have something focused and would be firing events. Reset rather
       * than merely suppressing the lift: a 390px cap on a 778px screen is the
       * other half of the customer's screenshot.
       */
      if (layout.visualViewportStale()) {
        reset();
        return;
      }
      /*
       * THE SAME CEILING AS ALWAYS — `min(92dvh, visible − 12)` — RESOLVED HERE
       * AND WRITTEN IN PX. The visual-viewport cap only ever LOWERS the old
       * ceiling; desktop and Android builds that resize the LAYOUT viewport
       * never get here at all, so they keep their native focus handling.
       *
       * ⚠️ Not a single `dvh` in what this hook writes, ever again. The two
       * terms are equal to `min(92dvh, …)` on the engines that matter: where the
       * layout viewport resizes for the keyboard (Chrome/Android) `dvh` IS
       * `innerHeight`, and on iOS, where it does not, `visibleHeight() − 12` is
       * the smaller term anyway whenever this branch runs. What changes is that
       * Chrome 101 can now USE it — see `SHEET_MAX_RATIO` for what a `dvh` in
       * here did to the panel instead.
       */
      const max = `${Math.min(
        Math.round(window.innerHeight * SHEET_MAX_RATIO),
        Math.round(visibleHeight() - 12),
      )}px`;
      setMax(max);
      setLift(`${inset()}px`);
      startPoll();
      renderDebug();
    };

    /*
     * Bring the focused field into the visible part of the sheet.
     *
     * Capping the height makes the panel scrollable, but its `scrollTop` stays 0
     * when the keyboard opens — so the field the user just tapped sits BELOW the
     * keyboard while the sheet shows its own top. iOS scrolls a focused element
     * into view for the page, not for an `overflow-y:auto` panel inside a fixed
     * overlay; we have to do it. Reported on the first fix's rollout 2026-09-05:
     * «нажимаю на поле карты — приходится пальцем вниз листать».
     *
     * Centring keeps the field mid-panel with its label and the error line
     * visible, rather than flush against the keyboard. Deferred one frame so it
     * runs AFTER the new max-height has laid out; on `focusin` also after the
     * keyboard's ~300ms slide, since `resize` is what fires then and the first
     * attempt would measure the pre-keyboard viewport.
     *
     * ⚠️ A field that is ALREADY visible is left alone — see `centreInScroller`.
     * This fires right after the tap that focused it, and a field that moves
     * between two taps is a tap that misses.
     */
    const centreFocused = () => {
      /*
       * ⚠️ RE-MEASURE FIRST, always.
       *
       * The centre is `scrollTop + (fieldTop − bodyTop) − (bodyHeight − fieldHeight)/2`,
       * so it is only right if the BODY is already the short, keyboard-sized
       * one. At `focusin` it is not: WebKit shrinks the visual viewport a frame
       * or two AFTER the focus, so a centre computed there is a centre for the
       * TALL sheet and the field lands below the pinned footer — «нажимаю на
       * поле карты, а оно под клавиатурой» (Igor, iPhone 15, 2026-09-21).
       * `apply()` is memoised and writes nothing when the numbers have not
       * moved, so calling it here costs nothing on the paths that already ran it.
       */
      apply();
      if (!keyboardShown()) return;
      const el = document.activeElement;
      if (!(el instanceof HTMLElement)) return;
      if (!el.matches('input, textarea, select')) return;
      requestAnimationFrame(() => centreInScroller(el, scrollerRef?.current));
    };

    /*
     * THE KEYBOARD-SETTLE WINDOW.
     *
     * One placement per focus is not enough on iOS. The keyboard slides for
     * ~300ms and WebKit reports the new height in STEPS (and pans the visual
     * viewport between them, which fires `scroll`, not `resize`), so the first
     * measurement after a tap is of a viewport that is still moving. For a
     * short window after each focus every viewport change re-places the field,
     * and one final pass runs once the height has stopped changing.
     *
     * Outside the window only `resize` re-places, and never `scroll`: iOS
     * fires `scroll` on every frame of a pinch/pan, and scrolling the sheet
     * under the buyer's own finger is the «дёргает» this file exists to avoid.
     */
    const SETTLE_WINDOW_MS = 1000;
    const SETTLE_MS = 300;
    let settleUntil = 0;
    let settleTimer = 0;
    const settling = () => Date.now() < settleUntil;

    const onResize = () => {
      // The visual viewport just spoke: whatever it reports now is live, not a
      // leftover. `visualViewportStale()` is judged against this timestamp.
      layout.vvReported();
      // Real geometry replaces the assumption the moment either viewport moves.
      if (assumed && (keyboardUp() || window.innerHeight !== assumedAtInnerH)) assumed = 0;
      // A GROWING layout viewport is the keyboard leaving — the one event that
      // teaches this sheet how tall its page is without one. `apply()` notes it
      // too; doing it here as well means a `resize` that ends in `reset()` still
      // updates the baseline the assumption is judged against.
      layout.note();
      apply();
      centreFocused();
      if (settling()) {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(centreFocused, SETTLE_MS);
      }
    };
    const onScroll = () => {
      apply();
      if (settling()) centreFocused();
    };
    /*
     * The same handler, but it also stamps the visual viewport as having spoken.
     * ⚠️ Bound ONLY to `visualViewport.scroll`, never to the window's: a document
     * scroll says nothing about whether `visualViewport.height` is still live, and
     * treating it as proof would keep a stale reading trusted forever.
     */
    const onVvScroll = () => {
      layout.vvReported();
      onScroll();
    };
    /*
     * THE GEOMETRY WATCH (2026-09-23).
     *
     * Two things move the viewport WITHOUT an event this hook can rely on:
     *
     * - iOS pans the visual viewport to reveal a field tapped while the
     *   keyboard is already up — «Номер карты», then «Срок» — and that pan
     *   changes `offsetTop`. With the lift still computed for `offsetTop = 0`
     *   the sheet is lifted by the full inset: ~130px of page shows between
     *   its bottom and the keyboard, its header is off the top, and only the
     *   field row and the footer are visible (Igor, iPhone, Safari,
     *   2026-09-23 09:32).
     * - the keyboard closes as a step changes and its fields unmount (card →
     *   SMS code): the closing `resize` may land after the blur timer, and a
     *   stale lift leaves a keyboard-sized band under a sheet with nothing
     *   left to type in (same report: «после перехода к вводу смс клава вниз
     *   уходит и свободного спейса снизу много»).
     *
     * So for `WATCH_MS` after every focus change the geometry is simply
     * re-read on a short timer. `apply()` is memoised and writes nothing when
     * the numbers have not moved, and re-placing the field is limited to the
     * settle window as before — the watch only adds READS.
     */
    const WATCH_MS = 1500;
    const WATCH_EVERY_MS = 100;
    let watchUntil = 0;
    let watchTimer = 0;
    const watchTick = () => {
      watchTimer = 0;
      const key = geometryKey();
      if (key !== seen) {
        seen = key;
        if (assumed && (keyboardUp() || window.innerHeight !== assumedAtInnerH)) assumed = 0;
        apply();
        if (settling()) centreFocused();
      }
      if (Date.now() < watchUntil) watchTimer = window.setTimeout(watchTick, WATCH_EVERY_MS);
    };
    const watch = () => {
      watchUntil = Date.now() + WATCH_MS;
      seen = geometryKey();
      if (!watchTimer) watchTimer = window.setTimeout(watchTick, WATCH_EVERY_MS);
    };
    let focusTimer = 0;
    let assumeTimer = 0;
    let focusH0 = 0;
    let focusV0 = 0;
    const maybeAssume = () => {
      if (keyboardUp()) return;
      if (measured) return;
      /*
       * ⚠️ THE LAYOUT VIEWPORT HAS ALREADY SHRUNK — there IS a keyboard and the
       * engine has handled it by resizing the page. Nothing to assume and
       * nothing to lift: a bottom sheet is fixed to the bottom of a viewport
       * that is now 448px tall, which is where it already is.
       *
       * This is the whole of «модалка слишком вверх поднимается» (Igor,
       * 2026-09-26). The test below only compares against the focus-time
       * snapshot, and a KEYBOARD SWAP moves it by less than `KEYBOARD_MIN_PX`:
       * numeric pad → text keys + suggestion strip is 534 → 448, 86px, so every
       * shrink test in this function was false while a keyboard filled 40% of
       * the screen. The assumption then wrote `--sheet-lift: 202px`.
       *
       * The in-app WebViews this whole path exists for resize NOTHING, so their
       * `innerHeight` never leaves its tallest value and they still reach the
       * assumption. `onKeyboardGeometry` is deliberately NOT guarded the same
       * way: it is a MEASUREMENT (`measured`, and it returns above), and a page
       * with `overlaysContent` set has no resize left to detect.
       */
      layout.note();
      if (layout.shrank()) return;
      /*
       * ⚠️ AND NEVER ONCE THIS ENGINE HAS PROVED IT RESIZES FOR THE KEYBOARD.
       * The assumption's premise is «a keyboard is up and nothing moved». A
       * browser that has been SEEN to move `innerHeight` for a keyboard will do
       * it again, so «nothing moved» there means «no keyboard», not «a keyboard
       * we cannot measure». Some host apps' WebViews resize and some do not, and
       * the UA cannot tell them apart — only watching can.
       */
      if (layout.resizesForKeyboard()) return;
      /*
       * ⚠️ NOT THIS ENGINE. iOS shrinks the visual viewport for a real keyboard,
       * so `keyboardUp()` sees one whenever there is one, and «focused, nothing
       * shrank» there means NO keyboard. Guessing one on an iPhone lifted a sheet
       * 299px off an idle page — see `watchUserGestures` for the recording.
       */
      if (!androidWebView) return;
      /*
       * ⚠️ AND NOT WITHOUT A TAP. The premise is «the user tapped a field», not
       * «something is focused»: a programmatic `.focus()` (the card modal's
       * `revealField` after a declined card, autofill, a focus on mount) opens no
       * keyboard anywhere. Same recording.
       */
      if (!gestures.recent()) return;
      // Either viewport shrank since the focus: a real keyboard, handled by
      // geometry (iOS) or by `dvh` itself (Chrome's layout resize).
      if (window.innerHeight < focusH0 - KEYBOARD_MIN_PX || vv.height < focusV0 - KEYBOARD_MIN_PX) {
        return;
      }
      if (!focusedField()) return;
      if (!(window.matchMedia?.('(pointer: coarse)').matches ?? false)) return;
      assumed = Math.round(window.innerHeight * ASSUMED_KEYBOARD_RATIO);
      assumedAtInnerH = window.innerHeight;
      centreFocused();
      /*
       * …and once more after the layout has caught up. Measured in Chromium
       * (2026-09-22): a `getBoundingClientRect` inside the very next animation
       * frame still returned the sheet at its OLD height — the custom-property
       * change had been written but not yet applied — so the first centring
       * saw the field as visible and did nothing. No `resize` follows an
       * assumed keyboard to trigger the settle path, so it is scheduled here.
       */
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(centreFocused, SETTLE_MS);
    };
    const onFocusIn = () => {
      settleUntil = Date.now() + SETTLE_WINDOW_MS;
      // The lift depends on there being a focused field, so a field taking focus
      // while the keyboard is ALREADY up has to be honoured at once — waiting for
      // the 350 ms backstop would show one frame of un-lifted sheet under the keys.
      apply();
      watch();
      window.clearTimeout(focusTimer);
      focusH0 = window.innerHeight;
      focusV0 = vv.height;
      window.clearTimeout(assumeTimer);
      assumeTimer = window.setTimeout(maybeAssume, ASSUME_KEYBOARD_AFTER_MS);
      // The backstop for a keyboard that opens without a single `resize`
      // (a hardware keyboard, an Android build that resizes the layout
      // viewport instead) — the behaviour every caller had before.
      focusTimer = window.setTimeout(centreFocused, 350);
    };
    /*
     * A blur is not what decides — `apply()` still reads the geometry — but it
     * is a cheap extra CHANCE to read it. The keyboard's close animation runs
     * after the blur, and a build that fires no closing `resize` at all would
     * otherwise keep the lift until the next viewport event of any kind, which
     * on a finished form is never.
     */
    let blurTimer = 0;
    const onFocusOut = (e: FocusEvent) => {
      const from = e.target;
      if (
        from instanceof HTMLElement &&
        (from.matches('input, textarea, select') || from.isContentEditable)
      ) {
        fieldBlurredAt = Date.now();
      }
      window.clearTimeout(assumeTimer);
      watch();
      apply();
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => {
        // Typing is over (no field took the focus back): the assumed keyboard
        // has closed. A real one is still read from the geometry by `apply`.
        if (assumed && !focusedField()) assumed = 0;
        apply();
      }, 350);
    };

    /*
     * Android WebViews (Instagram's in-app browser) resize NOTHING when the
     * keyboard opens: no `resize`, no pan, the keys simply cover the bottom of
     * the page. The 45% assumption above keeps the footer clear of the tallest
     * keyboards, and on a shorter one — Instagram's own is ~40% of a 20:9
     * phone — that shows as a band of page between the sheet and the keys
     * (tester, Android Instagram, 2026-09-23). Chromium exposes the exact
     * rectangle through the VirtualKeyboard API once the page opts into
     * `overlaysContent`: on the builds that have it the assumption becomes a
     * measurement, and the keyboard's own «hide» button — which blurs nothing
     * and fires no viewport event — is finally observable, so the sheet gets
     * its height back (the same tester: «окно не разворачивается обратно»).
     * Opted into only where the assumption applies: outside a WebView, Chrome
     * on Android resizes the layout viewport itself and `dvh` already fits.
     */
    const vk = androidWebView ? virtualKeyboardOf(navigator) : null;
    const prevOverlays = vk ? vk.overlaysContent : false;
    const onKeyboardGeometry = () => {
      if (!vk) return;
      const h = Math.round(vk.boundingRect?.height ?? 0);
      window.clearTimeout(assumeTimer);
      if (h >= KEYBOARD_MIN_PX && !keyboardUp()) {
        measured = true;
        assumed = h;
        assumedAtInnerH = window.innerHeight;
        apply();
        centreFocused();
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(centreFocused, SETTLE_MS);
      } else if (h < KEYBOARD_MIN_PX && measured) {
        measured = false;
        assumed = 0;
        apply();
      }
    };
    if (vk) {
      try {
        vk.overlaysContent = true;
      } catch {
        /* read-only on an older build: the assumption stays in charge */
      }
      vk.addEventListener('geometrychange', onKeyboardGeometry);
    }
    /*
     * Back from another app (Android Chrome, 2026-09-23: minimise, restore —
     * the keyboard is gone, the sheet still holds its shape): the closing
     * `resize` was delivered to a hidden page, if it came at all. Re-read the
     * geometry for a moment, and drop an assumed keyboard no field is waiting
     * for.
     */
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (assumed && !measured && !focusedField()) assumed = 0;
      apply();
      watch();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);

    apply();
    // `resize` fires when the keyboard opens/closes; `scroll` when iOS pans the
    // visual viewport to keep a focused field visible.
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onVvScroll);
    // A build that scrolls the DOCUMENT to reveal the field (rather than
    // panning the visual viewport) reports it here, not on `visualViewport`.
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onVvScroll);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
      if (vk) {
        vk.removeEventListener('geometrychange', onKeyboardGeometry);
        try {
          vk.overlaysContent = prevOverlays;
        } catch {
          /* as above */
        }
      }
      window.clearTimeout(focusTimer);
      window.clearTimeout(settleTimer);
      window.clearTimeout(blurTimer);
      window.clearTimeout(assumeTimer);
      window.clearTimeout(watchTimer);
      stopPoll();
      gestures.stop();
      if (debugEl) debugEl.remove();
      root.style.removeProperty('--sheet-max');
      root.style.removeProperty('--sheet-lift');
    };
    // `scrollerRef` is a ref object and therefore stable; it is listed only to
    // satisfy exhaustive-deps, and re-running on a new ref identity is correct
    // anyway (the listeners would otherwise keep scrolling the old element).
  }, [open, scrollerRef]);
}
